import React, {
  useMemo,
  useState,
  createContext,
  PropsWithChildren,
} from 'react';

import { Feature } from 'types';
import { LinkProps } from 'components';
import { useAppSelector } from 'store';
import { isBookingRole } from 'utils/booking';
import { isTimeOffApproverRole } from 'utils/time-off';
import { useTrainingMeQuery } from 'store/api/training';
import { RouteObject, getRouteObjects } from 'config/route-objects';
import { selectAuthUserRole, selectAuthUserFeatures } from 'store/slices/auth';

interface SidebarContextProps {
  isOpened: boolean;
  links: LinkProps[];
  setOpened: (isOpened: boolean) => void;
}

const getInitialState = (): SidebarContextProps => ({
  links: [],
  isOpened: false,
  setOpened: () => {},
});

export const SidebarContext =
  createContext<SidebarContextProps>(getInitialState());

export const SidebarContextProvider = ({ children }: PropsWithChildren) => {
  const [isOpened, setOpened] = useState(getInitialState().isOpened);
  const authUserFeatures = useAppSelector(selectAuthUserFeatures);
  const userRole = useAppSelector(selectAuthUserRole);
  // TIME OFF: always on for the approver roles (owner, 25 Oct 2026: no
  // company switch).
  const showTimeOff = isTimeOffApproverRole(userRole);
  // REP PERFORMANCE: the role's rep_scoreboard key (Company Settings >
  // Permissions; owner 25 Oct: "need to add them to permissions").
  const showRepPerformance = Boolean(
    authUserFeatures?.includes(Feature.REP_SCOREBOARD)
  );
  // TRAINING: the training key (every trainer's reps), and anyone who trains
  // a rep, whatever the role (the server answers both in /training/me).
  const { data: training } = useTrainingMeQuery(undefined, {
    skip: !userRole,
  });
  const showTraining = Boolean(training?.canSeeAll || training?.isTrainer);
  // BOOKING: always on for the Booking roles (owner, 25 Oct 2026: no
  // company switch).
  const showBooking = isBookingRole(userRole);

  const links = useMemo(() => {
    const mapLinks = (items: RouteObject[]): LinkProps[] => {
      return items
        .filter(
          ({
            path,
            title,
            features,
            timeOffOnly,
            bookingOnly,
            hideForRoles,
            trainingOnly,
            hideInSidebar,
            repPerformanceOnly,
          }) => {
            if (
              !path ||
              !title ||
              hideInSidebar ||
              (timeOffOnly && !showTimeOff) ||
              (repPerformanceOnly && !showRepPerformance) ||
              (trainingOnly && !showTraining) ||
              (bookingOnly && !showBooking) ||
              (userRole && hideForRoles?.includes(userRole))
            ) {
              return false;
            }

            return features
              ? features.some((feature) => authUserFeatures?.includes(feature))
              : true;
          }
        )
        .map(({ icon, path, title, children: linkChildren }) => ({
          icon,
          path,
          title,
          items: linkChildren ? mapLinks(linkChildren) : undefined,
        })) as LinkProps[];
    };

    return mapLinks(getRouteObjects());
  }, [
    authUserFeatures,
    showTimeOff,
    showBooking,
    showTraining,
    showRepPerformance,
  ]);

  const value = {
    links,
    isOpened,
    setOpened,
  };

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
};
